
// src/api.js

// Fallback to local dev API if the env var is missing
const BASE_URL = (
  (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:5500/api"
).replace(/\/$/, "");

// --- WA-P0-01: staff session token storage ---
const TOKEN_KEY = "fixspire_whatsapp_staff_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}
export function isAuthenticated() {
  return Boolean(getToken());
}

// ✅ Staff login -- stores the returned token on success
export async function login(username, password) {
  const res = await fetch(`${BASE_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || "Login failed.");
  }
  setToken(data.token);
  return data;
}

// --- internal helper ---
async function request(path, options = {}) {
  const token = getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
    ...options,
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    const err = new Error(`${res.status} ${res.statusText}: ${txt || "Request failed"}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// ✅ Fetch all stored messages
export async function fetchMessages() {
  try {
    return await request("/messages");
  } catch (err) {
    if (err.status === 401) {
      // Session missing/expired -- clear it and let the caller (App.js) show the login screen.
      clearToken();
      throw err;
    }
    console.error("❌ Error fetching messages:", err);
    return [];
  }
}

// ✅ Send a regular text message
export async function sendMessage(to, message) {
  try {
    return await request("/send-message", {
      method: "POST",
      body: JSON.stringify({ to, message }),
    });
  } catch (err) {
    console.error("❌ Error sending message:", err);
    throw err;
  }
}

// ✅ Send a Meta-verified template message
//    variables = array of strings that fill {{placeholders}} in the template
export async function sendTemplate(to, templateName, variables = []) {
  try {
    return await request("/send-template", {
      method: "POST",
      body: JSON.stringify({ to, templateName, variables }),
    });
  } catch (err) {
    console.error("❌ Template send error:", err);
    throw err;
  }
}

// ✅ Send media (images, PDFs, etc.)
//    mediaType = "image" | "document" | "video" | "audio"
//    mediaUrl must be a public URL accessible by Meta
export async function sendMedia(to, mediaUrl, mediaType = "document", caption = "") {
  try {
    return await request("/send-media", {
      method: "POST",
      body: JSON.stringify({ to, mediaUrl, mediaType, caption }),
    });
  } catch (err) {
    console.error("❌ Media send error:", err);
    throw err;
  }
}
